import React, { useRef, useEffect } from 'react';
import './AnimatedLogo.css';

interface AnimatedLogoProps {
  height?: number;
  animate?: boolean;
  onAnimationEnd?: () => void;
  responsive?: boolean;
}

const AnimatedLogo: React.FC<AnimatedLogoProps> = ({ 
  height = 180, 
  animate = false,
  onAnimationEnd,
  responsive = false
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const rocketRef = useRef<SVGGElement>(null);

  useEffect(() => {
    const svg = svgRef.current;
    const rocket = rocketRef.current;
    if (!svg || !rocket) return;

    let isAnimating = false;
    let isTransitioning = false;
    let mouseLeaveTimeout: NodeJS.Timeout;

    const handleMouseEnter = () => {
      // Clear any pending mouse leave
      if (mouseLeaveTimeout) {
        clearTimeout(mouseLeaveTimeout);
        mouseLeaveTimeout = null as any;
      }
      
      // Don't start new animation if we're still transitioning
      if (isTransitioning) return;
      
      isAnimating = true;
      rocket.style.animation = 'rocket-loop 1.5s linear infinite';
    };

    const handleMouseLeave = () => {
      if (!isAnimating || isTransitioning) return;
      
      // Debounce mouse leave to prevent rapid switching
      mouseLeaveTimeout = setTimeout(() => {
        if (!isAnimating) return; // Double check
        
        isAnimating = false;
        isTransitioning = true;
      
      // Get current transform
      const computedStyle = window.getComputedStyle(rocket);
      const matrix = new DOMMatrix(computedStyle.transform);
      const currentX = matrix.m41;
      const currentY = matrix.m42;
      
      // Stop animation immediately
      rocket.style.animation = 'none';
      
      // Force browser to recognize the animation stop
      void (rocket as any).offsetHeight;
      
      // Set current position
      rocket.style.transform = `translate(${currentX}px, ${currentY}px)`;
      rocket.style.opacity = computedStyle.opacity;
      
      // Check if rocket is in a good position to animate out
      const distance = Math.sqrt(currentX * currentX + currentY * currentY);
      const isVisible = parseFloat(computedStyle.opacity) > 0.5;
      
      if (distance < 200 && isVisible) {
        // Rocket is visible - animate it flying out
        rocket.style.transition = 'transform 0.3s linear, opacity 0.3s ease-out';
        
        requestAnimationFrame(() => {
          rocket.style.transform = `translate(${currentX * 2}px, ${currentY * 2}px)`;
          rocket.style.opacity = '0';
        });
        
        // After it's gone, bring new rocket from opposite side
        setTimeout(() => {
          rocket.style.transition = 'none';
          rocket.style.transform = 'translate(-100%, 100%)';
          rocket.style.opacity = '0';
          
          // Force reflow
          void (rocket as any).offsetHeight;
          
          requestAnimationFrame(() => {
            rocket.style.transition = 'transform 0.5s ease-out, opacity 0.4s ease-out';
            rocket.style.transform = 'translate(0, 0)';
            rocket.style.opacity = '1';
          });
        }, 300);
      } else {
        // Rocket is not in a good position - just reset it
        rocket.style.transition = 'none';
        rocket.style.transform = 'translate(-100%, 100%)';
        rocket.style.opacity = '0';
        
        // Force reflow
        void (rocket as any).offsetHeight;
        
        requestAnimationFrame(() => {
          rocket.style.transition = 'transform 0.5s ease-out, opacity 0.4s ease-out';
          rocket.style.transform = 'translate(0, 0)';
          rocket.style.opacity = '1';
        });
      }
      
      // Cleanup
      setTimeout(() => {
        rocket.style.transition = '';
        rocket.style.transform = '';
        rocket.style.opacity = '';
        isTransitioning = false;
      }, 800);
      }, 100); // 100ms debounce delay
    };

    const container = svg.closest('.navbar-brand-box');
    if (container) {
      container.addEventListener('mouseenter', handleMouseEnter);
      container.addEventListener('mouseleave', handleMouseLeave);
      
      return () => {
        container.removeEventListener('mouseenter', handleMouseEnter);
        container.removeEventListener('mouseleave', handleMouseLeave);
        if (mouseLeaveTimeout) clearTimeout(mouseLeaveTimeout);
      };
    }
  }, []);

  // For responsive mode, use 100% width and auto height
  const svgProps = responsive 
    ? { width: "100%", height: "auto", style: { maxHeight: height } }
    : { width: height, height: height };

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 1024 1024"
      {...svgProps}
      className={animate ? 'logo-animated' : ''}
      onAnimationEnd={onAnimationEnd}
    >
      {/* Sonne (bleibt stehen) */}
      <ellipse
        className="sun"
        style={{ fill: '#fac339', fillOpacity: 1 }}
        cx="514.1875"
        cy="503.43564"
        rx="366.7244"
        ry="367.19653"
      />
      
      {/* Sterne (bleiben stehen) */}
      <g className="stars">
        {/* Gelbe Sterne */}
        <g transform="translate(34,16)">
          <path
            d="M 119 186 C 114.318 188.378 109.621 195.178 115.805 198.956 C 121.665 202.83 128.314 205.482 133.142 210.801 C 137.405 214.657 139.708 220.351 141.341 225.435 C 142.682 231.141 150.74 233.773 154.65 229 C 158.057 224.464 158.84 218.405 162.97 214.277 C 168.557 207.09 176.835 203.038 184.198 198.047 C 189.902 194.902 188.259 187.218 182.828 184.776 C 175.462 180.381 166.52 176.658 163.072 168.104 C 160.671 162.597 158.728 155.015 152.116 153.379 C 146.259 153.586 143.98 160.219 141.399 164.448 C 138.481 170.521 134.122 175.781 128.491 179.507 C 125.439 181.83 122.249 183.964 119 186 Z"
            style={{ fill: '#fcc43a', fillOpacity: 0.96 }}
          />
        </g>
        <g transform="translate(34,16)">
          <path
            d="M 91 263 C 91.24 264.55 93.61 268.7 95 270 C 104.53 277.09 116.63 267.19 111.25 256.75 C 105.87 246.3 90.23 252.01 91 263 Z"
            style={{ fill: '#fac53b', fillOpacity: 0.92 }}
          />
        </g>
        <g transform="translate(2,2)">
          <path
            d="M 908.173 439.298 C 910.809 443.18 911.695 449.075 916.686 450.75 C 920.834 451.382 922.113 446.385 922.921 443.327 C 923.529 438.038 928.189 435.758 932.047 433.126 C 935.481 430.551 935.469 424.429 930.857 423.183 C 926.832 422.032 924.032 418.665 922.588 414.854 C 921.221 411.856 920.258 406.631 915.828 407.764 C 911.593 408.702 911.794 413.707 910.3 416.877 C 908.065 421.464 903.275 423.732 899.593 426.914 C 897.554 430.112 901.155 433.094 903.241 435.127 C 904.823 436.59 906.525 437.913 908.173 439.298 Z"
            style={{ fill: '#fac439', fillOpacity: 0.93 }}
          />
        </g>
        <g transform="translate(6,22)">
          <path
            d="M 814 745 C 816.27 745.38 818.72 744.7 821 745 C 823.07 743.9 826.7 742.01 828 740 C 833.29 730.3 823.2 718.72 813.25 723.25 C 803.29 727.78 804.9 742.12 814 745 Z"
            style={{ fill: '#f8c439', fillOpacity: 0.93 }}
          />
        </g>
      </g>
      
      {/* Rakete (fliegt weg) */}
      <g className="rocket" ref={rocketRef}>
        {/* Hauptkörper der Rakete */}
        <path
          d="M 506.684 287.491 C 469.358 323.153 415.391 323.81 367.189 319.981 C 328.69 315.344 296.838 339.023 273.352 366.743 C 250.484 392.646 204.808 424.513 218.119 460.736 C 258.126 495.462 326.271 433.462 352.349 471.428 C 377.085 516.496 293.955 536.961 257.75 554.526 C 198.013 574.871 173.767 638.633 162.235 695.191 C 153.168 737.147 140.148 779.697 110.105 811.686 C 73.7358 849.092 121.141 880.984 159.499 869.231 C 220.182 863.118 281.07 850.734 338.022 828.73 C 384.279 809.362 419.822 769.855 435.855 722.527 C 450.057 695.23 468.81 636.221 508.528 656.661 C 540.465 681.898 521.927 724.694 519.235 758.266 C 506.261 802.566 545.736 813.947 571.976 783.423 C 601.205 761.055 629.808 733.112 653.044 704.705 C 685.062 655.463 675.52 601.057 682.116 549.227 C 852.838 359.331 886.191 308.765 919.906 142.159 C 921.203 132.907 921.999 111.511 905.89 105.259 C 879.743 95.1108 827.806 104.832 794.534 113.523 C 686.004 147.48 603.137 202.018 506.684 287.491 Z"
          style={{ fill: '#03071b', fillOpacity: 1 }}
        />
        
        {/* Alle anderen Teile der Rakete */}
        <g fill="#576268" fillOpacity="1.00">
          <path d="M 748.164 154.134 C 743.673 157.558 727.775 158.811 735.223 166.93 C 740.269 171.411 744.645 176.333 749.355 180.985 C 762.26 194.203 760.666 175.841 766.979 173.227 C 775.078 170.104 781.588 164.297 789.336 160.563 C 814.051 147.48 838.828 134.117 864.939 124.09 C 869.181 123.546 871.058 121.596 865.576 122.282 C 835.628 125.363 783.243 138.283 748.164 154.134 Z" />
        </g>
        <g fill="#3A4851" fillOpacity="1.00">
          <path d="M 752.181 183.456 C 773.314 201.871 790.408 224.657 811.427 243.213 C 823.085 253.987 833.891 265.678 843.722 278.033 C 860.074 288.178 889.277 177.843 892.4 136.19 C 890.609 120.365 871.112 119.744 859.391 125.52 C 826.362 140.382 794.075 156.912 762.786 175.078 C 758.596 176.871 753.88 178.8 752.181 183.456 Z" />
        </g>
        <g fill="#70797F" fillOpacity="1.00">
          <path d="M 385.597 482.809 C 412.481 541.255 499.77 384.386 547.872 332.827 C 592.131 290.004 636.094 254.859 688.599 222.106 C 702.698 214.601 725.752 193.797 726.877 194.638 C 727.969 195.603 724.81 192.733 715.786 183.862 C 696.059 164.183 655.521 197.874 638.285 211.532 C 574.058 262.426 509.289 325.68 456.179 391.313 C 436.141 416.167 404.024 454.461 385.597 482.809 Z" />
        </g>
        <g fill="#3B4952" fillOpacity="1.00">
          <path d="M 726.079 193.921 C 711.203 202.712 695.016 210.009 680.645 220.118 C 634.49 249.42 591.006 283.283 551.959 321.474 C 528.508 343.123 506.458 366.439 484.466 389.598 C 452.749 422.897 423.446 458.503 392 492 C 401.586 502.114 416.584 530.823 428.554 523.658 C 461.843 491.578 492.119 462.28 528.636 433.958 C 554.37 414.186 572.553 439.512 554.558 458.061 C 526.517 489.555 489.895 534.275 461 565 C 478.942 580.62 495.825 622.059 525.789 600.076 C 578.871 565.497 632.407 528.428 679.318 485.751 C 705.572 462.645 728.053 432.645 752.808 408.057 C 779.956 372.061 815.317 345.689 832.43 312.972 C 837.744 310.026 817.225 288.109 805.759 274.457 C 784.809 252.815 764.541 230.861 743.462 209.361 C 731.011 196.905 726.153 194.008 726.079 193.921 Z" />
        </g>
        <g fill="#12262A" fillOpacity="1.00">
          <path d="M 701 247 C 654.87 240.41 615.9 271.07 600.98 312.98 C 586.06 354.88 614.02 407.27 659.98 412.02 C 705.93 416.77 743.63 388.89 759.02 347.02 C 774.42 305.16 745.5 253.36 701 247 Z" />
        </g>
        <g fill="#F8FAFA" fillOpacity="1.00">
          <path d="M 622 336 C 620.672 353.971 629.574 372.467 645.204 381.749 C 651.516 385.423 658.816 388.834 666.299 387.775 C 666.85 385.375 658.541 384.918 655.718 383.498 C 647.861 381.192 639.583 378.287 634.529 371.413 C 624.94 360.135 621.555 344.436 624 330 C 639.473 330.838 653.014 321.683 665.147 313.216 C 674.455 306.742 684.098 299.014 687.913 287.935 C 689.925 282.28 687.115 275.907 682 273 C 695.493 270.594 708.198 278.217 718.289 286.36 C 723.254 290.236 727.48 294.972 732.205 299.085 C 729.865 293.653 727.511 290.053 723.235 284.264 C 713.953 274.937 700.469 270.168 687.371 270.945 C 665.897 270.179 646.06 283.243 634.214 300.485 C 627.022 310.894 622.246 323.257 622 336 Z" />
        </g>
        <g fill="#DAEAF4" fillOpacity="1.00">
          <path d="M 639 376 C 652.78 380.13 673.06 372.23 684.23 366.23 C 695.4 360.23 709.023 350.6 716.193 340.3 C 723.373 330.01 733.92 317.126 734 304.176 C 727.97 283.066 703.23 268.39 682 273 C 698.05 283.58 681 301.98 668.7 310.7 C 656.4 319.41 640.47 331.15 624 330 C 621.22 346.86 626.07 364.5 639 376 Z" />
        </g>
        <g fill="#26363D" fillOpacity="1.00">
          <path d="M 737 422.791 C 672.638 497.712 589.581 552.885 507.435 606.273 C 525.946 608.969 547.596 615.995 561.79 599.31 C 660.021 535.636 753.943 458.791 813.73 356.44 C 821.769 344.573 836.377 324.937 832.843 309.065 C 823.39 292.001 809.342 328.286 803.019 336.345 C 783.555 366.954 763.173 397.424 737 422.791 Z" />
        </g>
        <g fill="#A4B6C1" fillOpacity="1.00">
          <path d="m 639,376 c 28.27483,27.04765 67.21971,7.35587 81.51471,-7.55113 13.547,-14.177 23.56429,-52.25687 11.56129,-69.35887 -1.512,12.245 -17.536,50.639 -49.032,67.664 -11.735,6.118 -24.864,10.334 -38.168,10.299 -1.988,-0.145 -3.965,-0.481 -5.876,-1.053 z" />
        </g>
        <g fill="#556067" fillOpacity="1.00">
          <path d="M 238 440 C 246.13 435.75 254.54 428.08 262.75 420.75 C 270.95 413.41 278.97 405.72 287.7 398.7 C 296.42 391.67 303.47 383.64 312.75 376.75 C 322.03 369.87 328.98 362.63 339.7 356.7 C 350.41 350.76 366.94 351.43 380.07 350.07 C 393.21 348.72 409.61 347.57 423 346 C 436.4 344.43 439.747 342.21 452.657 340.54 C 419.227 340.55 387.47 341.77 355 340 C 322.53 338.23 307.436 362.661 287.626 382.641 C 267.816 402.611 255.73 419.25 238 440 Z" />
        </g>
        <g fill="#3B4A52" fillOpacity="1.00">
          <path d="M 238 440 C 245.365 459.593 272.147 436.36 287.881 437.49 C 313.402 431.33 342.237 430.925 364.941 445.832 C 386.491 453.636 398.847 430.576 408.872 415.945 C 426.668 392.858 445.817 370.788 463.49 347.65 C 456.327 332.124 426.47 346.524 409.546 346.344 C 379.884 348.873 345.913 345.218 322.222 366.871 C 293.022 389.167 268.555 419.405 238 440 Z" />
        </g>
        <g fill="#3D4B53" fillOpacity="1.00">
          <path d="M 408 548 C 398.08 539.02 391.32 532.07 382.25 521.75 C 373.18 511.44 359.52 532.73 365.7 543.3 C 371.87 553.88 383.35 560.03 392 568 C 398.07 562.2 404.17 555.31 408 548 Z" />
        </g>
        <g fill="#3D4B53" fillOpacity="1.00">
          <path d="M 663 560 C 625.69 586.06 590.17 615.02 551 638 C 566.17 686.1 542.32 733.57 535 780 C 558.19 764.03 577.66 747.37 599.25 728.25 C 620.83 709.13 644.638 690.063 649.22 662.22 C 654.333 631.151 661.29 592.52 663 560 Z" />
        </g>
        <g fill="#F17C35" fillOpacity="1.00">
          <path d="M 191.18 833.019 C 252.981 825.122 316.669 809.574 366.87 770.819 C 391.404 750.667 415.598 726.207 421.792 693.782 C 425.436 677.401 423.027 657.741 421.837 640.968 C 420.267 627.841 415.451 627.629 411.926 641.174 C 405.66 654.051 399.61 666.969 392.706 679.525 C 379.475 709.433 348.418 725.479 319.508 737.001 C 297.255 747.465 273.832 755.094 250.487 762.624 C 242.932 763.437 217.488 776.605 228.276 760.011 C 242.262 735.699 258.827 711.757 263.087 683.386 C 268.46 658.841 272.928 632.093 290.147 612.735 C 301.082 602.161 315.372 594.531 330.114 591.037 C 341.571 588.483 356.342 583.682 363.117 575.096 C 348.816 566.753 330.953 568.79 315 569.058 C 283.467 570.594 250.332 583.284 232.148 610.301 C 213.169 635.618 197.813 664.144 191.556 695.385 C 179.309 744.536 157.616 804.068 123.482 842.601" />
        </g>
        <g fill="#FCC63B" fillOpacity="1.00">
          <path d="M 356 572 C 351.31 574.648 325.604 581.666 299.863 603.716 C 294.348 608.441 289.38 612.95 284.92 618.92 C 280.46 624.89 277.64 631.95 274.92 638.92 C 267.944 652.209 261.487 697.574 251.23 719.23 C 240.513 741.86 221.395 767.377 223.635 769.573 C 242.896 768.783 262.893 759.276 282.02 753.02 C 288 751.11 293.241 748.501 299.221 746.221 C 305.211 743.951 311.04 741.77 317.07 739.07 C 323.11 736.37 327.831 733.991 334.221 731.221 C 340.621 728.451 343.18 727.12 350.08 723.08 C 356.98 719.05 357 719.33 365.23 713.23 C 385.498 698.714 395.194 676.381 406 655 C 410.06 647.14 413.41 638.99 419 632 C 419.759 631.473 409.818 618.933 407 615 C 397.69 621.38 382.631 631.029 373.311 618.699 C 363.981 606.359 375.06 592.74 380 582 C 375.121 579.31 360.118 572.814 356 572 Z" />
        </g>
        <g fill="#3D4B53" fillOpacity="1.00">
          <path d="M 440 581 C 434.38 586.42 428.11 592.51 424 599 C 440.89 614.02 462.37 650.58 487 630 C 471.99 613.24 455.58 597.48 440 581 Z" />
        </g>
      </g>
    </svg>
  );
};

export default AnimatedLogo;